import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { T } from "../../constants";
import CameraIcon from './CameraIcon'
import CheckIcon from './CheckIcon'

const useStyles = makeStyles({
  /* Photo tile */
  photoTile: { position: "relative", aspectRatio: "4 / 3", overflow: "hidden", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", padding: 0, "&:disabled": { cursor: "default" } },
  /* cover, so a 16/9 camera frame fills the 4/3 tile without letterboxing */
  photoImage: { position: "absolute", inset: 0, backgroundSize: "cover", backgroundPosition: "center" },
  photoScrim: { position: "absolute", inset: 0, background: "rgba(0,0,0,0.28)" },
  photoTileOverlay: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 },
  photoCheckCircle: { display: "flex", height: 40, width: 40, alignItems: "center", justifyContent: "center", borderRadius: "50%", background: T.success, color: T.successFg },
  photoCapturedLabel: { fontSize: 12, fontWeight: 600, color: T.successText },
  photoLabel: { fontSize: 12, fontWeight: 500, color: T.mutedFg },
  onImageLabel: { fontSize: 12, fontWeight: 600, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.5)" },
  retakePill: { position: "absolute", right: 6, bottom: 6, borderRadius: 999, background: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px 8px", fontSize: 10, fontWeight: 600, textTransform: "uppercase" },
  progressPct: { fontSize: 13, fontWeight: 600, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.5)" },
  progressTrack: { position: "absolute", left: 0, right: 0, bottom: 0, height: 4, background: "rgba(255,255,255,0.25)" },
  progressBar: { height: "100%", background: T.primary, transition: "width .15s linear" },
  errorText: { fontSize: 11, color: T.danger, textAlign: "center", padding: "0 8px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  retryBtn: { borderRadius: 6, border: "none", background: T.danger, color: T.dangerFg, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
});

function PhotoTile({ label, imageUrl, status = "idle", progress = 0, error, onCapture, onRetry }) {
  const s = useStyles();

  const done = status === "done";
  const uploading = status === "uploading";
  const failed = status === "error";

  const border = done
    ? `1px solid ${T.success}`
    : failed
    ? `1px solid ${T.danger}`
    : `1px dashed ${T.border}`;

  const background = done
    ? T.successTint
    : failed
    ? T.dangerTint
    : T.surface;

  return (
    <button
      onClick={onCapture}
      disabled={uploading}
      className={s.photoTile}
      style={{ border, background }}
      aria-label={done ? `Retake ${label} photo` : `Take ${label} photo`}
      title={done ? `Retake the ${label.toLowerCase()} photo` : undefined}
    >
      {imageUrl && (
        <div
          className={s.photoImage}
          /* faded while uploading, and faded hard on failure so the red error
             text stays readable over the photo */
          style={{
            backgroundImage: `url(${imageUrl})`,
            opacity: uploading ? 0.55 : failed ? 0.2 : 1,
          }}
        />
      )}
      {imageUrl && done && <div className={s.photoScrim} />}

      <div className={s.photoTileOverlay}>
        {done ? (
          <>
            <div className={s.photoCheckCircle}>
              <CheckIcon size={20} color={T.successFg} />
            </div>
            <div className={imageUrl ? s.onImageLabel : s.photoCapturedLabel}>{label}</div>
          </>
        ) : uploading ? (
          <div className={s.progressPct}>{Math.round(progress * 100)}%</div>
        ) : failed ? (
          <>
            <div className={s.errorText}>{error || "Upload failed."}</div>
            <span
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onRetry();
                }
              }}
              /* stopPropagation, or the tile's own onCapture reopens the camera */
              onClick={(e) => { e.stopPropagation(); onRetry(); }}
              className={s.retryBtn}
            >
              Retry
            </span>
          </>
        ) : (
          <>
            <CameraIcon size={28} color={T.mutedFg} />
            <div className={s.photoLabel}>{label}</div>
          </>
        )}
      </div>

      {done && <div className={s.retakePill}>Retake</div>}

      {uploading && (
        <div className={s.progressTrack}>
          <div className={s.progressBar} style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}
    </button>
  );
}

export default PhotoTile;
